import { useEffect, useRef, useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'
import { HelpCenterTranslations } from '@/HelpCenter/ui/helpCenterTranslations'
import { useHelpCenter } from '@/HelpCenter/ui/context/HelpCenterContext'
import { useUserData } from '@/User/ui/context'
import { handleErrors } from '@/Shared/ui/form'
import {
    OreCardContainer,
    OreCheckbox,
    OreFileUpload,
    OreInput,
    OreStack,
    OreSwitch,
    OreText,
    OreTextArea,
    SvgNavBarNotificactions,
    UploadedFile
} from '@edenredespana/oreneta'
import { navigationRoutes } from "@/Shared/ui/routers"
import { useNavigate } from 'react-router-dom'
import { useDownloaderHook } from '@/Shared/ui/downloader'
import { LegalDocumentType } from '@/Shared/domain'
import { AcceptConditionsOptions } from '@/Account/domain/navbarModel'
import { navigationTranslation } from '@/Shared/ui/navigation/navigationTranslation'

export const ReopenTicketForm = (): JSX.Element => {
    const { downloadPublicDocument } = useDownloaderHook()
    const { t } = useTranslation()
    const { reopenForm, setHasFileError, uploadedFiles, setUploadedFiles, ticketDetail } = useHelpCenter()
    const [displayAlternativeContact, setDisplayAlternativeContact] = useState(false)
    const { user } = useUserData()

    const navigate = useNavigate()
    const { errors } = handleErrors(reopenForm.formState, reopenForm.getFieldState)

    const handleMyAccountClick = () => {
        navigate(navigationRoutes.configuration)
    }

    const options: AcceptConditionsOptions[] = [
        {
            name: t(navigationTranslation.header.user.privacyPolicyFileName),
            documentId: LegalDocumentType.privacyPolicies
        },

    ]

    const handleClickConditions = (option: AcceptConditionsOptions) => {
        const { documentId, name } = option
        downloadPublicDocument(documentId, name)
    }

    const handleFilesChange = (files: UploadedFile[]) => {
        const totalSize = files.reduce((sum, f) => sum + f.size, 0)
        if (files.length !== uploadedFiles.length) {
            hasSizeOverflowRef.current = false
        }
        setHasFileError(totalSize > MAX_TOTAL_SIZE || hasSizeOverflowRef.current)
        setUploadedFiles(files)
        const convertFiles = async () => {
            const caseFiles = await Promise.all(
                files.map(async (uploadedFile) => {
                    const base64 = await new Promise<string>((resolve) => {
                        const reader = new FileReader()
                        reader.onloadend = () => {
                            const result = reader.result as string
                            resolve(result.split(',')[1])
                        }
                        reader.readAsDataURL(uploadedFile.file)
                    })
                    return { name: uploadedFile.name, file: base64 }
                })
            )
            reopenForm.setValue('caseFiles', caseFiles)
        }
        convertFiles()
    }

    const MAX_TOTAL_SIZE = 25 * 1024 * 1024
    const ACCEPTED_EXTENSIONS = ['.jpeg', '.jpg', '.png', '.xls', '.xlsx', '.docx', '.pdf'];
    const hasSizeOverflowRef = useRef(false)

    const handleFileInputCapture = (e: React.FormEvent<HTMLDivElement>) => {
        const target = e.target as HTMLInputElement
        if (target.type !== 'file' || !target.files || target.files.length === 0) return

        const currentSize = uploadedFiles.reduce((sum, f) => sum + f.size, 0)
        let newTotal = currentSize
        for (let i = 0; i < target.files.length; i++) {
            newTotal += target.files[i].size
        }
        hasSizeOverflowRef.current = newTotal > MAX_TOTAL_SIZE
        if (newTotal > MAX_TOTAL_SIZE) {
            setHasFileError(true)
        }
    }

    useEffect(() => {
        if (ticketDetail?.otherEmail || ticketDetail?.otherPhone) {
            setDisplayAlternativeContact(true)
        } else {
            setDisplayAlternativeContact(false)
        }
    }, [ticketDetail])

    return (
        <form >
            <OreStack space="medium" placeContent="stretch">
                <OreStack space="xsmall" placeContent="stretch">
                    <OreText size="text-lg" bold>{t(HelpCenterTranslations.step4.title)}</OreText>
                    <OreText>{t(HelpCenterTranslations.step4.subtitle)}</OreText>
                </OreStack>
                <OreStack space="small" placeContent="stretch">
                    <OreInput
                        {...reopenForm.register('subject')}
                        {...errors('subject')}
                        id="subject"
                        label={t(HelpCenterTranslations.step4.subject.label)}
                        placeholder={t(HelpCenterTranslations.step4.subject.placeholder)}
                        legend={t(HelpCenterTranslations.step4.subject.legend)}
                        maxLength={100}
                        disabled
                    />
                    <OreTextArea
                        {...reopenForm.register('description')}
                        {...errors('description')}
                        id="description"
                        label={t(HelpCenterTranslations.step4.description.label)}
                        placeholder={t(HelpCenterTranslations.step4.description.placeholder)}
                        legend={t(HelpCenterTranslations.step4.description.legend)}
                        maxLength={1000}
                        rows={5}
                        disabled
                    />
                    <OreTextArea
                        {...reopenForm.register('expandedDescription')}
                        {...errors('expandedDescription')}
                        id="expandedDescription"
                        label={t(HelpCenterTranslations.reopenTicket.expandedDescription.label)}
                        placeholder={t(HelpCenterTranslations.reopenTicket.expandedDescription.placeholder)}
                        legend={t(HelpCenterTranslations.reopenTicket.expandedDescription.legend)}
                        maxLength={1000}
                        rows={5}
                    />
                    <div onChangeCapture={handleFileInputCapture}>
                        <OreFileUpload
                            buttonText={t(HelpCenterTranslations.step4.caseFiles.label)}
                            onFilesChange={handleFilesChange}
                            files={uploadedFiles}
                            fileTypeError={t(HelpCenterTranslations.step4.form.invalidFileType)}
                            totalSizeError={
                                <Trans
                                    t={t}
                                    i18nKey={HelpCenterTranslations.step4.form.maxFileSize}
                                    components={[
                                        <a href="mailto:info@es.edenred.info" />
                                    ]}
                                />
                            }
                            acceptedFormats={ACCEPTED_EXTENSIONS}
                            validationLegend={t(HelpCenterTranslations.step4.caseFiles.legend)}
                        />
                    </div>
                </OreStack>
                <OreStack space="medium" placeContent="stretch">
                    <OreStack space="2xsmall" placeContent="stretch">
                        <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.title)}</OreText>
                        <OreText size="text-sm">{t(HelpCenterTranslations.step4.contactData.description)}</OreText>
                    </OreStack>
                    <OreCardContainer>
                        <div style={{ padding: '1.5rem' }}>
                            <OreStack space="medium" placeContent="stretch">
                                <OreStack direction="row" space="2xlarge" placeContent="normal">
                                    <OreStack space="2xsmall">
                                        <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.email)}</OreText>
                                        <OreText size="text-md" bold>{user?.user?.email}</OreText>
                                    </OreStack>
                                    <OreStack space="2xsmall">
                                        <OreText size="text-md">{t(HelpCenterTranslations.step4.contactData.phone)}</OreText>
                                        <OreText size="text-md" bold>{user?.user?.phoneNumber}</OreText>
                                    </OreStack>
                                </OreStack>
                                <OreStack direction="row" placeItems="center"
                                    style={{
                                        backgroundColor: "#F1F5F9",
                                        borderRadius: "8px",
                                        border: "1px solid #CBD5E1",
                                        padding: "1rem"
                                    }}>
                                    <OreStack
                                        direction="row"
                                        placeItems="center"
                                        style={{
                                            paddingRight: "8px"
                                        }}>
                                        <SvgNavBarNotificactions />
                                        <OreText tone="neutral-600" >
                                            <Trans t={t}>
                                                {t(HelpCenterTranslations.step4.contactData.infoMessage)}
                                            </Trans>
                                            {' '}
                                            <a
                                                onClick={handleMyAccountClick}
                                                style={{ textDecoration: 'none', fontWeight: 'bold', cursor: 'pointer' }}>
                                                {t(HelpCenterTranslations.step4.contactData.changeAccessMode)}
                                            </a>
                                        </OreText>
                                    </OreStack>
                                </OreStack>
                            </OreStack>
                        </div>
                    </OreCardContainer>
                    <OreSwitch
                        label={
                            <OreText
                                tone='neutral-900'
                                size='text-md'>
                                {t(HelpCenterTranslations.step4.contactData.alternativeContact)}
                            </OreText>}
                        name="useAlternativeContact"
                        checked={displayAlternativeContact}
                        onChange={() => {
                            setDisplayAlternativeContact(!displayAlternativeContact)
                            if (displayAlternativeContact) {
                                reopenForm.setValue('otherEmail', '', { shouldValidate: true })
                                reopenForm.setValue('otherPhone', '', { shouldValidate: true })
                            } else {
                                reopenForm.setValue('otherEmail', ticketDetail?.otherEmail || '', { shouldValidate: true })
                                reopenForm.setValue('otherPhone', ticketDetail?.otherPhone || '', { shouldValidate: true })
                            }
                        }}
                        disabled
                    />
                    {displayAlternativeContact && (
                        <OreStack space="small" direction="row" placeContent="stretch">
                            <OreInput
                                {...reopenForm.register('otherEmail')}
                                {...errors('otherEmail')}
                                id="otherEmail"
                                type="email"
                                placeholder={t(HelpCenterTranslations.step4.contactData.alternativeEmailPlaceholder)}
                                label={t(HelpCenterTranslations.step4.contactData.alternativeEmail)}
                                disabled
                            />
                            <OreInput
                                {...reopenForm.register('otherPhone')}
                                {...errors('otherPhone')}
                                id="otherPhone"
                                type="tel"
                                placeholder={t(HelpCenterTranslations.step4.contactData.alternativePhonePlaceholder)}
                                label={t(HelpCenterTranslations.step4.contactData.alternativePhone)}
                                legend={t(HelpCenterTranslations.step4.contactData.alternativePhoneLegend)}
                                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                                    if (!/[0-9]/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) {
                                        e.preventDefault()
                                    }
                                }}
                                disabled
                            />
                        </OreStack>
                    )}
                </OreStack>
                <OreCheckbox
                    {...reopenForm.register('acceptPrivacyPolicy')}
                    {...errors('acceptPrivacyPolicy')}
                    label={
                        <OreText
                            size='text-md'
                            tone='neutral-900'>
                            <Trans
                                t={t}
                                components={[
                                    <a
                                        onClick={() => handleClickConditions(options[0])}
                                        style={{ cursor: 'pointer' }}
                                    />,
                                ]}>
                                {t(HelpCenterTranslations.step4.privacyPolicy.label)}
                            </Trans>
                        </OreText>
                    }
                />
            </OreStack>
        </form>
    )
}


